import { GetReportSettingsMenuList } from 'hooks'
import { useTabletAndBelowMediaQuery } from 'layout'
import {
    CompanyScreen,
    ReportSettingsScreen,
    ThemeScreen,
    UserScreen,
    AnnouncementScreen,
} from 'page-components'
import { useEffect, useMemo, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Segment } from 'semantic-ui-react'
import { SettingsHome } from './SettingsHome'
import styled from 'styled-components'
import {
    SettingsTabs,
    InviteUserService,
    InviteType,
    isPlanLimitErrorCode,
    getPlanLimitCode,
    canManageUsersByRole,
} from 'utility'
import { CustomMultipleResponseScreen } from 'page-components/settings/tabs/CustomMultipleResponse'
import { AppPrimaryButton } from 'elements'
import { AddInviteModal } from 'page-components/group/Modal'
import { TemplateGroupScreen } from 'page-components/settings/tabs/TemplateGroup'
import { MenuPermissionScreen } from './../../page-components/settings/tabs/company-menu-permission'
import { InvoiceSettingsScreen } from 'page-components/settings/tabs/invoice'
import { ImpersonationHistoryScreen } from 'page-components/settings/tabs/impersonation/ImpersonationHistory'
import { EscalationPolicyList } from 'page-components/escalation-policy'
import { ActionPlanCategoryList } from 'page-components/action-plan-category'
import { isActionPlanCategoryAdmin } from 'page-components/action-plan-category/utils'
import { CompanyTicketSetting } from 'page-components/auto-ticket/CompanyTicketSetting'
import { AccountPlanScreen } from 'screens/account'
import { useNotification } from 'hooks/toast'
import { useAuth } from 'context'
import { useSubscriptionUsage } from 'context/subscription-usage'
import { ENABLE_ACCOUNT_PLAN_PAGE } from 'utility/subscription-usage'
import { HomeDashboardSettings } from 'page-components/settings/tabs/home-dashboard/HomeDashboardSettings'
import { VoiceInspectionSettings } from 'page-components/settings/tabs/voice/VoiceInspectionSettings'
import {
    isHomeDashboardAdmin,
    isHomeDashboardEligible,
    useHomeDashboardStatus,
} from 'utility/home-dashboard'

const SettingContainer = styled.div``

const Detail = styled.div``

const DetailBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
    flex-wrap: wrap;
`

const BackButton = styled.button`
    border: 0;
    background: transparent;
    color: #57534e;
    font-weight: 700;
    font-size: 14px;
    cursor: pointer;
    padding: 8px 0;

    &:hover {
        color: #1c1917;
    }
`

export const SettingsScreen = () => {
    const { successToast, errorToast } = useNotification()
    const { user } = useAuth()
    const { refetchUsage } = useSubscriptionUsage()
    const isSuperAdmin = user?.IsSuperAdmin
    const isTicketSettingsAdmin = isActionPlanCategoryAdmin(user)
    // Voice inspection: admins of companies in the server allowlist (login Capabilities.CanUseVoice).
    const showVoiceTab = isTicketSettingsAdmin && user?.Capabilities?.CanUseVoice === true
    const [inviteModalOpen, setInviteModalOpen] = useState(false)
    const [userListKey, setUserListKey] = useState(0)
    const announcementRef = useRef(null)
    const params = useParams()
    const navigate = useNavigate()
    const isMobile = useTabletAndBelowMediaQuery()
    const { data: reportSettingsMenuList } = GetReportSettingsMenuList()
    // Home dashboard tab: company admins of companies the backend allows; never Cintas / franchise.
    const homeEligible = isHomeDashboardEligible(user) && isHomeDashboardAdmin(user)
    const homeStatus = useHomeDashboardStatus(homeEligible ? user : null)
    const showHomeDashboardTab = homeEligible && homeStatus?.available === true

    const panes = useMemo(
        () => [
            {
                menuItem: 'User',
                key: SettingsTabs.User,
                render: () => <UserScreen key={userListKey} />,
            },
            {
                menuItem: 'Company List',
                key: SettingsTabs.Company,
                render: () => <CompanyScreen />,
            },
            {
                menuItem: 'Report Settings',
                key: SettingsTabs.Report,
                render: () => (
                    <ReportSettingsScreen
                        reportSettingsMenuList={reportSettingsMenuList}
                    />
                ),
            },
            ...(ENABLE_ACCOUNT_PLAN_PAGE
                ? [
                      {
                          menuItem: 'Account & Plan',
                          key: SettingsTabs.AccountPlan,
                          render: () => (
                              <Segment raised>
                                  <AccountPlanScreen />
                              </Segment>
                          ),
                      },
                  ]
                : []),
            {
                menuItem: 'Theme',
                key: SettingsTabs.Theme,
                render: () => <ThemeScreen />,
            },
            {
                menuItem: 'Template Group',
                key: SettingsTabs.TemplateGroup,
                render: () => <TemplateGroupScreen />,
            },
            {
                menuItem: 'Announcement',
                key: SettingsTabs.Announcement,
                render: () => <AnnouncementScreen ref={announcementRef} />,
            },
            {
                menuItem: 'Invoice',
                key: SettingsTabs.SettingInvoice,
                render: () => <InvoiceSettingsScreen />,
            },
            {
                menuItem: 'Menu Permission',
                key: SettingsTabs.MenuPermission,
                render: () => <MenuPermissionScreen />,
            },
            {
                menuItem: 'Custom Multiple Response',
                key: 'custom-multiple-response',
                render: () => (
                    <Segment raised>
                        <CustomMultipleResponseScreen />
                    </Segment>
                ),
            },
            {
                menuItem: 'Escalation Policies',
                key: SettingsTabs.EscalationPolicies,
                render: () => (
                    <Segment raised>
                        <EscalationPolicyList />
                    </Segment>
                ),
            },
            {
                menuItem: 'Ticket Categories',
                key: SettingsTabs.TicketCategories,
                render: () => (
                    <Segment raised>
                        <ActionPlanCategoryList />
                    </Segment>
                ),
            },
            ...(isTicketSettingsAdmin
                ? [
                      {
                          menuItem: 'Corrective-action tickets',
                          key: SettingsTabs.CorrectiveActionTickets,
                          render: () => (
                              <Segment raised>
                                  <CompanyTicketSetting />
                              </Segment>
                          ),
                      },
                  ]
                : []),
            ...(showVoiceTab
                ? [
                      {
                          // Admins only, like the server (user/Voice/CompanySetting).
                          menuItem: 'Voice Inspection',
                          key: SettingsTabs.VoiceInspection,
                          render: () => <VoiceInspectionSettings />,
                      },
                  ]
                : []),
            ...(showHomeDashboardTab
                ? [
                      {
                          menuItem: 'Home Dashboard',
                          key: SettingsTabs.HomeDashboard,
                          render: () => <HomeDashboardSettings />,
                      },
                  ]
                : []),
            ...(isSuperAdmin
                ? [
                      {
                          menuItem: 'Impersonation History',
                          key: SettingsTabs.ImpersonationHistory,
                          render: () => (
                              <Segment raised>
                                  <ImpersonationHistoryScreen />
                              </Segment>
                          ),
                      },
                  ]
                : []),
        ],
        [reportSettingsMenuList, isSuperAdmin, isTicketSettingsAdmin, userListKey, showHomeDashboardTab, showVoiceTab],
    )

    useEffect(() => {
        if (!params.tab) return
        if (
            !ENABLE_ACCOUNT_PLAN_PAGE &&
            params.tab === SettingsTabs.AccountPlan
        ) {
            navigate('/app/settings', { replace: true })
            return
        }
        if (params.tab === SettingsTabs.InvoicePrefix) {
            navigate(
                `/app/settings/${SettingsTabs.SettingInvoice}?invoiceTab=prefix`,
                { replace: true },
            )
            return
        }
        if (params.tab === SettingsTabs.InvoiceRules) {
            navigate(
                `/app/settings/${SettingsTabs.SettingInvoice}?invoiceTab=rules`,
                { replace: true },
            )
            return
        }
        if (params.tab === SettingsTabs.CompanyMenuPermission) {
            navigate(
                `/app/settings/${SettingsTabs.MenuPermission}?permissionTab=company`,
                { replace: true },
            )
            return
        }
        if (params.tab === SettingsTabs.UserMenuPermission) {
            navigate(
                `/app/settings/${SettingsTabs.MenuPermission}?permissionTab=user`,
                { replace: true },
            )
        }
    }, [params, navigate])

    const handleInviteSubmit = async formData => {
        try {
            if (formData && formData.InviteTo) {
                await InviteUserService.sendInvitation([
                    {
                        InviteTo: formData.InviteTo,
                        InviteType: InviteType.Email,
                        FullName: formData.FullName,
                        CustomerIds: formData.CustomerIds || null,
                        TemplateIds: formData.TemplateIds || null,
                        RoleId: formData.RoleId || '',
                        AccessMode: formData.AccessMode || 'Inherit',
                        UserPermissionOverrides:
                            formData.UserPermissionOverrides || [],
                        LoginSource: formData.LoginSource || 1,
                        ShowPhotoGps:
                            typeof formData.ShowPhotoGps === 'boolean'
                                ? formData.ShowPhotoGps
                                : true,
                    },
                ])
                successToast('Member invitation has been sent')
                refetchUsage()
                setUserListKey(key => key + 1)
            }
        } catch (error) {
            const data = error?.error || {}
            const code = getPlanLimitCode(data)
            if (!(error?.status === 403 && isPlanLimitErrorCode(code))) {
                console.log('error', error)
                errorToast('Failed to send invitation')
            }
        } finally {
            setInviteModalOpen(false)
        }
    }

    const activeKey = params.tab
    const activePane = panes.find(pane => pane.key === params.tab)

    const renderHeaderAction = () => {
        if (activeKey === SettingsTabs.Announcement) {
            return (
                <AppPrimaryButton
                    onClick={() => announcementRef.current?.handleCreate()}
                >
                    Add Announcement
                </AppPrimaryButton>
            )
        }
        if (activeKey === SettingsTabs.AccountPlan) {
            return null
        }
        if (activeKey === SettingsTabs.EscalationPolicies) {
            return null
        }
        if (activeKey === SettingsTabs.CorrectiveActionTickets) {
            return null
        }
        if (activeKey === SettingsTabs.VoiceInspection) {
            return null
        }
        if (!canManageUsersByRole(user)) {
            return null
        }
        return (
            <AppPrimaryButton onClick={() => setInviteModalOpen(true)}>
                Invite Team Member
            </AppPrimaryButton>
        )
    }

    const showInvite = canManageUsersByRole(user)

    return (
        <SettingContainer className={`mb-5 ${isMobile && 'px-0'}`}>
            {!params.tab || !activePane ? (
                <SettingsHome
                    items={panes.map(pane => ({
                        key: pane.key,
                        label: pane.menuItem,
                    }))}
                    showInvite={showInvite}
                    onInvite={() => setInviteModalOpen(true)}
                    onOpen={key => navigate(`/app/settings/${key}`)}
                />
            ) : (
                <Detail>
                    <DetailBar>
                        <BackButton type="button" onClick={() => navigate('/app/settings')}>
                            ← All settings
                        </BackButton>
                        {renderHeaderAction()}
                    </DetailBar>
                    {activePane.render()}
                </Detail>
            )}
            <AddInviteModal
                open={inviteModalOpen}
                onClose={handleInviteSubmit}
                loading={false}
            />
        </SettingContainer>
    )
}
